"use strict";
/** *******************************************************************************************************************
  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
  
  Licensed under the Apache License, Version 2.0 (the "License").
  You may not use this file except in compliance with the License.
  You may obtain a copy of the License at
  
      http://www.apache.org/licenses/LICENSE-2.0
  
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.                                                                              *
 ******************************************************************************************************************** */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAppLayoutContext = void 0;
const react_1 = __importStar(require("react"));
const core_1 = require("@material-ui/core");
const clsx_1 = __importDefault(require("clsx"));
const react_use_localstorage_1 = __importDefault(require("react-use-localstorage"));
const Menu_1 = __importDefault(require("@material-ui/icons/Menu"));
const InfoOutlined_1 = __importDefault(require("@material-ui/icons/InfoOutlined"));
const IconButton_1 = __importDefault(require("@material-ui/core/IconButton"));
const Box_1 = __importDefault(require("../../layouts/Box"));
const Sidebar_1 = __importStar(require("./components/Sidebar"));
const Stack_1 = __importDefault(require("../../layouts/Stack"));
const Overlay_1 = __importDefault(require("../../components/Overlay"));
const Flashbar_1 = __importDefault(require("../../components/Flashbar"));
const useScrollPosition_1 = __importDefault(require("../../hooks/useScrollPosition"));
const LoadingIndicator_1 = __importDefault(require("../../components/LoadingIndicator"));
const constants_1 = require("./constants");
const useStyles = core_1.makeStyles((theme) => ({
    root: {
        margin: '0',
        overflow: 'hidden',
    },
    flashbarContainer: {
        margin: theme.spacing(-4, -4, 2, -4),
    },
    breadcrumbsContainer: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
        },
    },
    main: {
        display: 'flex',
        height: ({ headerHeightInPx }) => `calc(100vh - ${headerHeightInPx}px)`,
    },
    content: ({ hasSideNavigation, isSideNavigationOpen, hasHelpPanel, isHelpPanelOpen }) => ({
        marginTop: 0,
        marginBottom: 0,
        height: '100%',
        flexGrow: 1,
        position: 'relative',
        overflow: 'auto',
        boxSizing: 'border-box',
        marginLeft: hasSideNavigation && !isSideNavigationOpen ? -WIDTH_SIDEBAR : 0,
        marginRight: hasHelpPanel && !isHelpPanelOpen ? -WIDTH_HELP_PANEL : 0,
    }),
    notifications: ({ mainContentScrollPosition }) => ({
        position: 'absolute',
        top: mainContentScrollPosition.y || 0,
        left: mainContentScrollPosition.x || 0,
        right: 0,
        zIndex: theme.zIndex.modal,
        transition: 'all 0.5s linear',
    }),
    contentPadding: {
        [theme.breakpoints.up('sm')]: {
            padding: `${theme.spacing(2)}px ${theme.spacing(4)}px`,
        },
    },
    mainContent: ({ notificationsBoxHeight }) => ({
        marginTop: notificationsBoxHeight,
        '&:focus': {
            outline: 'none',
        },
    }),
    menu: {
        [theme.breakpoints.down('xs')]: {
            position: 'absolute',
        },
        [theme.breakpoints.up('sm')]: {
            position: 'absolute',
            top: '20px',
            paddingRight: 0,
        },
    },
    menuBar: {
        display: 'flex',
        boxShadow: '0 2px 1px -1px rgba(0,28,36,.3)',
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
    menuBarIcon: {
        padding: theme.spacing(2),
    },
    menuBarNavIcon: {
        flexShrink: 1,
        borderRight: `1px solid ${theme.palette.grey['400']}`,
    },
    menuBarInfoIcon: {
        flexShrink: 1,
        borderLeft: `1px solid ${theme.palette.grey['400']}`,
    },
}));
const WIDTH_SIDEBAR = 285;
const WIDTH_HELP_PANEL = 285;
const initialState = {
    openHelpPanel: () => { },
    setHelpPanelContent: (content) => { },
};
const AppLayoutContext = react_1.createContext(initialState);
/**
 * Basic layout for application, with place holder for header, navigation area, content area, breadcrumbs and tools/help panel.
 * It should be placed as the top most component in main content area. There should not be any spacing around it, it consumes
 * 100% of the width and height of the main content area, providing its own scrolling behavior.
 * By default it comes with a padding inside the content region. It can be removed by setting prop paddingContentArea == false.
 */
const AppLayout = ({ children, header, navigation = null, helpPanel = null, breadcrumbs = null, paddingContentArea = true, maxNotifications = 2, inProgress = false, notifications, headerHeightInPx = 65, }) => {
    const [helpPanelContent, setHelpPanelContent] = react_1.useState(helpPanel);
    const [isSideNavigationOpen, setIsSideNavigationOpen] = react_use_localstorage_1.default(constants_1.LOCAL_STORAGE_KEY_SIDE_NAV_OPEN, 'false');
    const [isHelpPanelOpen, setIsHelpPanelOpen] = react_use_localstorage_1.default(constants_1.LOCAL_STORAGE_KEY_HELP_PANEL_OPEN, 'false');
    const notificationsBoxRef = react_1.useRef(null);
    const mainContentRef = react_1.useRef(null);
    const [notificationsBoxHeight, setNotificationsBoxHeight] = react_1.useState(0);
    const [mainContentScrollPosition, setMainContentScrollPosition] = react_1.useState({
        x: 0,
        y: 0,
    });
    react_1.useLayoutEffect(() => {
        var _a;
        setNotificationsBoxHeight(((_a = notificationsBoxRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight) || 0);
    }, [notificationsBoxRef, notifications]);
    const { handleScroll } = useScrollPosition_1.default((position) => {
        setMainContentScrollPosition(position);
    }, mainContentRef, 200);
    const classes = useStyles({
        hasSideNavigation: !!navigation,
        hasHelpPanel: !!helpPanelContent,
        isSideNavigationOpen: isSideNavigationOpen === 'true',
        isHelpPanelOpen: isHelpPanelOpen === 'true',
        inProgress,
        notificationsBoxHeight,
        mainContentScrollPosition,
        headerHeightInPx,
    });
    const renderNavigationIcon = react_1.useCallback((rootClassname) => {
        return (react_1.default.createElement(IconButton_1.default, { color: "inherit", "aria-label": "open drawer", "data-testid": "open-nav-drawer", onClick: () => setIsSideNavigationOpen('true'), classes: {
                root: rootClassname,
                label: classes.menu,
            } },
            react_1.default.createElement(Menu_1.default, null)));
    }, [classes, setIsSideNavigationOpen]);
    const renderInfoIcon = react_1.useCallback((rootClassname) => {
        return (react_1.default.createElement(IconButton_1.default, { color: "inherit", "aria-label": "open drawer", "data-testid": "open-helppanel-drawer", onClick: () => setIsHelpPanelOpen('true'), classes: {
                root: rootClassname,
                label: classes.menu,
            } },
            react_1.default.createElement(InfoOutlined_1.default, null)));
    }, [classes, setIsHelpPanelOpen]);
    const openHelpPanel = react_1.useCallback((open = true) => {
        setIsHelpPanelOpen(open.toString());
    }, [setIsHelpPanelOpen]);
    return (react_1.default.createElement(Box_1.default, { className: classes.root },
        react_1.default.createElement(AppLayoutContext.Provider, { value: {
                openHelpPanel,
                setHelpPanelContent,
            } },
            header,
            (navigation || helpPanelContent) && (react_1.default.createElement(Box_1.default, { className: classes.menuBar },
                navigation && (react_1.default.createElement(Box_1.default, { className: classes.menuBarNavIcon }, renderNavigationIcon(classes.menuBarIcon))),
                react_1.default.createElement(Box_1.default, { width: "100%" }),
                helpPanelContent && (react_1.default.createElement(Box_1.default, { className: classes.menuBarInfoIcon }, renderInfoIcon(classes.menuBarIcon))))),
            react_1.default.createElement(Box_1.default, { className: classes.main },
                navigation && (react_1.default.createElement(Sidebar_1.default, { sidebarWidth: WIDTH_SIDEBAR, isSidebarOpen: isSideNavigationOpen, setIsSidebarOpen: setIsSideNavigationOpen, type: Sidebar_1.SidebarType.SIDE_NAVIGATION, renderIcon: renderNavigationIcon }, navigation)),
                react_1.default.createElement("div", { ref: mainContentRef, className: classes.content, onScroll: handleScroll },
                    notifications && notifications.length > 0 && (react_1.default.createElement("div", { ref: notificationsBoxRef, className: classes.notifications },
                        react_1.default.createElement(Flashbar_1.default, { items: notifications, maxItemsDisplayed: maxNotifications }))),
                    react_1.default.createElement(Box_1.default, { tabIndex: 0, position: "relative", className: clsx_1.default(classes.mainContent, { [classes.contentPadding]: !!paddingContentArea }) },
                        react_1.default.createElement(Stack_1.default, { spacing: "s" },
                            breadcrumbs && react_1.default.createElement(Box_1.default, { className: classes.breadcrumbsContainer }, breadcrumbs),
                            react_1.default.createElement("main", null, children)),
                        inProgress && (react_1.default.createElement(Overlay_1.default, null,
                            react_1.default.createElement(LoadingIndicator_1.default, { size: "large" }))))),
                helpPanelContent && (react_1.default.createElement(Sidebar_1.default, { sidebarWidth: WIDTH_HELP_PANEL, isSidebarOpen: isHelpPanelOpen, setIsSidebarOpen: setIsHelpPanelOpen, type: Sidebar_1.SidebarType.HELP_PANEL, renderIcon: renderInfoIcon }, helpPanelContent))))));
};
const useAppLayoutContext = () => react_1.useContext(AppLayoutContext);
exports.useAppLayoutContext = useAppLayoutContext;
exports.default = AppLayout;
//# sourceMappingURL=index.js.map